import axios from "axios";
import storePersist, { localConstant } from "../localStorage";
import { enqueueSnackbar } from "notistack";
import { generateUUID, getDeviceName } from "../../utils";
import apiConfig from "./apiConfig";
import SnackbarConfig from "../../utils/snackbarConfig";
import { substituteUrlParams } from "./helper";

const checkIfTokenExpired = () => {
  if (!storePersist.get(localConstant.API_ACCESS_TOKEN)) return false;
  const tokenExpiresIn = Number(
    JSON.parse(JSON.stringify(storePersist.get(localConstant.USER_DATA)))
      .expires_in,
  );

  const currentTime = new Date().getTime();
  const loggedInTime = storePersist.get(localConstant.LOGIN_TIMESTAMP);
  if (!loggedInTime) return false;
  const timeDifference = (currentTime - loggedInTime) / 1000;
  return timeDifference >= tokenExpiresIn;
};

axios.interceptors.request.use(
  async function (config) {
    config.headers["country"] = "USA";
    config.headers["source"] = getDeviceName();
    config.headers["correlationId"] = generateUUID();

    const endPoint = config.url.split("/").reverse()[0];
    if (endPoint !== "login" && endPoint !== "refresh") {
      const isTokenExpired = checkIfTokenExpired();
      if (isTokenExpired) {
        const token = await refreshSession();
        config.headers["Authorization"] = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

export const getRequest = async (path, params) => {
  try {
    const response = await axios.get(path, {
      params,
      headers: {
        "Access-Control-Allow-Origin": "*",
      },
    });
    if (response?.status >= 200 && response?.status < 300) {
      return await response?.data;
    } else {
      throw new Error(`${response?.status}`);
    }
  } catch (error) {
    throw error;
  }
};

export const postRequest = async (path, data, params) => {
  try {
    const response = await axios.post(path, data, { params: { ...params } });
    if (response.status >= 200 && response.status < 300) {
      return await response.data;
    } else {
      throw new Error(`${response.status}`);
    }
  } catch (error) {
    throw error;
  }
};

export const deleteRequest = async (path, data, params) => {
  try {
    const response = await axios.delete(path, data, { params: { ...params } });
    if (response.status >= 200 && response.status < 300) {
      return await response.data;
    } else {
      throw new Error(`${response.status}`);
    }
  } catch (error) {
    throw error;
  }
};

export const postMultiFormDataAuthRequest = async (path, formData) => {
  try {
    const response = await axios({
      method: "post",
      url: path,
      data: formData,
      headers: {
        "Content-Type": "multipart/form-data",
        country: "USA",
        source: getDeviceName(),
        correlationId: generateUUID(),
        Authorization: `Bearer ${storePersist.get(
          localConstant.API_ACCESS_TOKEN,
        )}`,
      },
    });
    if (response.status >= 200 && response.status < 300) {
      return await response.data;
    } else {
      throw new Error(`${response.errMsg}`);
    }
  } catch (error) {
    throw error;
  }
};

export const postAuthenticationRequest = async (
  path,
  data,
  params,
  acceptHeader = "application/json",
  contentTypeHeader = "application/json",
) => {
  const headers = {
    "Content-Type": contentTypeHeader,
    country: "USA",
    source: getDeviceName(),
    correlationId: generateUUID(),
    Accept: acceptHeader,
    Authorization: `Bearer ${storePersist.get(localConstant.API_ACCESS_TOKEN)}`,
  };
  try {
    const response = await axios.post(path, data, {
      headers: headers,
    });
    if (response.status >= 200 && response.status < 300) {
      return await response.data;
    } else {
      throw new Error(`${response.errMsg}`);
    }
  } catch (error) {
    throw error;
  }
};

export const postAuthenticationRequestForAgreement = async (
  path,
  data,
  params,
  acceptHeader = "application/json",
  contentTypeHeader = "application/json",
) => {
  const headers = {
    "Content-Type": contentTypeHeader,
    country: "USA",
    source: getDeviceName(),
    correlationId: generateUUID(),
    Accept: acceptHeader,
    Authorization: `Bearer ${storePersist.get(localConstant.API_ACCESS_TOKEN)}`,
    language: "en",
  };
  try {
    const response = await axios.post(path, data, {
      headers: headers,
      // params: { ...params },
    });
    if (response.status >= 200 && response.status < 300) {
      return await response.data;
    } else {
      throw new Error(`${response.errMsg}`);
    }
  } catch (error) {
    throw error;
  }
};

export const postAuthenticationRequestWithoutAuth = async (
  path,
  data,
  params,
  acceptHeader = "application/json",
  contentTypeHeader = "application/json",
) => {
  const headers = {
    "Content-Type": contentTypeHeader,
    country: "USA",
    source: getDeviceName(),
    correlationId: generateUUID(),
    Accept: acceptHeader,
  };
  try {
    const response = await axios.post(path, data, {
      headers: headers,
    });
    if (response.status >= 200 && response.status < 300) {
      return await response.data;
    } else {
      throw new Error(`${response.errMsg}`);
    }
  } catch (error) {
    throw error;
  }
};

export const postMultiformdataAuthenticationRequest = async (
  path,
  formData,
) => {
  const headers = {
    "Content-Type": "multipart/form-data",
    country: "USA",
    source: getDeviceName(),
    correlationId: generateUUID(),
    Authorization: `Bearer ${storePersist.get(localConstant.API_ACCESS_TOKEN)}`,
  };
  try {
    const response = await axios.post(path, formData, {
      headers: headers,
    });
    if (response.status >= 200 && response.status < 300) {
      return await response.data;
    } else {
      throw new Error(`${response.errMsg}`);
    }
  } catch (error) {
    throw error;
  }
};

export const postAuthenticationRequestWithoutAccessToken = async (
  path,
  data,
  params,
  acceptHeader = "application/json",
  contentTypeHeader = "application/json",
) => {
  const headers = {
    "Content-Type": contentTypeHeader,
    Accept: acceptHeader,
  };
  try {
    const response = await axios.post(path, data, {
      headers: headers,
      params: { ...params },
    });
    if (response.status >= 200 && response.status < 300) {
      return await response.data;
    } else {
      throw new Error(`${response.errMsg}`);
    }
  } catch (error) {
    throw error;
  }
};

export const postAuthMultiPartRequest = async (path, data, params) => {
  const headers = {
    "Content-Type": "multipart/form-data",
    Authorization: `Bearer ${storePersist.get(localConstant.API_ACCESS_TOKEN)}`,
  };
  try {
    const response = await axios.post(path, data, {
      headers: headers,
      params: { ...params },
    });
    if (response.status >= 200 && response.status < 300) {
      return await response.data;
    } else {
      throw new Error(`${response.errMsg}`);
    }
  } catch (error) {
    throw error;
  }
};

export const getRequestAuthenticationRequest = async (path, params) => {
  try {
    const response = await axios.get(path, {
      params,
      headers: {
        country: "USA",
        source: getDeviceName(),
        correlationId: generateUUID(),
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${storePersist.get(
          localConstant.API_ACCESS_TOKEN,
        )}`,
      },
    });
    if (response.status >= 200 && response.status < 300) {
      return await response.data;
    } else {
      throw new Error(`${response.status}`);
    }
  } catch (error) {
    throw error;
  }
};

export const putAuthenticationRequest = async (path, params) => {
  try {
    const response = await axios.put(path, params, {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        country: "USA",
        source: getDeviceName(),
        correlationId: generateUUID(),
        Authorization: `Bearer ${storePersist.get(
          localConstant.API_ACCESS_TOKEN,
        )}`,
      },
    });
    if (response.status >= 200 && response.status < 300) {
      return await response.data;
    } else {
      throw new Error(`${response.status}`);
    }
  } catch (error) {
    throw error;
  }
};

export const getCopartRequest = async (path, params) => {
  try {
    const response = await axios.get(path, {
      params,
      headers: {
        "Access-Control-Allow-Origin": "http://localhost:3000",
        Authorization: `Bearer ${storePersist.get(
          localConstant.API_ACCESS_TOKEN,
        )}`,
        "Content-Type": "application/json",
        "Access-Control-Allow-Headers":
          "Origin, X-Requested-With, Content-Type, Accept, Authortization",
      },
      withCredentials: true,
    });
    if (response.status >= 200 && response.status < 300) {
      return await response.data;
    } else {
      throw new Error(`${response.status}`);
    }
  } catch (error) {
    throw error;
  }
};

export const getCopartMobileRequest = async (path, params) => {
  try {
    const response = await axios.get(path, {
      params,
      headers: {
        country: "USA",
        Authorization: `Bearer ${storePersist.get(
          localConstant.API_ACCESS_TOKEN_OFFLINE,
        )}`,
        "Content-Type": "application/json",
        source: "test",
      },
      withCredentials: true,
    });
    if (response.status >= 200 && response.status < 300) {
      return await response.data;
    } else {
      throw new Error(`${response.status}`);
    }
  } catch (error) {
    throw error;
  }
};

export const postAuthenticationGeoservices = async (path, data, params) => {
  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: `Bearer ${storePersist.get(localConstant.API_ACCESS_TOKEN)}`,
  };
  try {
    const response = await axios.post(path, data, {
      headers: headers,
      params: { ...params },
    });
    if (response.status >= 200 && response.status < 300) {
      return await response.data;
    } else {
      throw new Error(`${response.errMsg}`);
    }
  } catch (error) {
    throw error;
  }
};

export const deleteAuthenticationRequest = async (
  path,
  params,
  payload = {},
) => {
  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: `Bearer ${storePersist.get(localConstant.API_ACCESS_TOKEN)}`,
  };
  try {
    const response = await axios.delete(path, {
      headers: headers,
      params: { ...params },
      data: payload,
    });
    if (response.status >= 200 && response.status < 300) {
      return await response.data;
    } else {
      throw new Error(`${response.status}`);
    }
  } catch (error) {
    throw error;
  }
};

export const uploadImageCopartRequest = async (path, params) => {
  const headers = {
    "Content-Type": "multipart/form-data",
    Authorization: `Bearer ${storePersist.get(
      localConstant.API_ACCESS_TOKEN_OFFLINE,
    )}`,
    country: "USA",
    source: "STP-VEHICLE",
  };
  try {
    const response = await axios.post(path, params, {
      headers: headers,
    });
    if (response.status >= 200 && response.status < 300) {
      return await response.data;
    } else {
      throw new Error(`${response.errMsg}`);
    }
  } catch (error) {
    throw error;
  }
};

export const getBasicAuthRequest = async (path, params) => {
  try {
    const response = await axios.get(path, {
      params,
      headers: {
        "Access-Control-Allow-Origin": "*",
      },
    });
    if (response.status >= 200 && response.status < 300) {
      return await response.data;
    } else {
      throw new Error(`${response.status}`);
    }
  } catch (error) {
    throw error;
  }
};

const refreshSession = async () => {
  try {
    const response = await axios.post(apiConfig.refreshToken, {
      refreshToken: storePersist.get(localConstant.REFRESH_TOKEN),
    });
    if (response?.data?.entity_id) {
      console.log("I am inside resfresh session api's response handler");
      storePersist.set(
        localConstant.API_ACCESS_TOKEN,
        response?.data?.access_token,
      );
      storePersist.set(
        localConstant.REFRESH_TOKEN,
        response?.data?.refresh_token,
      );
      storePersist.set(localConstant.LOGIN_TIMESTAMP, new Date().getTime());
      storePersist.set(localConstant.USER_ID, response?.data?.entity_id);
      storePersist.set(
        localConstant.ENTITY_CUSTOMER_ID,
        response?.data?.entity_customer_id,
      );
      storePersist.set(
        localConstant.USER_PROFILE,
        response?.data?.entity_role_code,
      );
      storePersist.set(localConstant.USER_DATA, response?.data);
      return response?.data?.access_token;
    } else {
      enqueueSnackbar("Unable to refresh token. Please sign in again !", {
        variant: "error",
        autoHideDuration: 3000,
        action: SnackbarConfig,
      });
      return "";
    }
  } catch (error) {
    if (error?.response?.status !== 200) {
      storePersist.clear();
      window.location.replace(window.location.origin + "/login");
    }
    enqueueSnackbar("Unable to refresh token. Please sign in again !", {
      variant: "error",
      autoHideDuration: 3000,
      action: SnackbarConfig,
    });
    throw error;
  }
};

export const getInvoiceListRequest = async (path, params) => {
  try {
    const response = await axios.get(path, {
      params,
      headers: {
        country: "USA",
        source: getDeviceName(),
        correlationId: generateUUID(),
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${storePersist.get(
          localConstant.API_ACCESS_TOKEN,
        )}`,
      },
    });
    if (response.status >= 200 && response.status < 300) {
      return await response.data;
    } else {
      throw new Error(`${response.status}`);
    }
  } catch (error) {
    throw error;
  }
};

export const getpaymentListRequest = async (path, params) => {
  try {
    const response = await axios.get(path, {
      params,
      headers: {
        country: "USA",
        source: getDeviceName(),
        correlationId: generateUUID(),
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${storePersist.get(
          localConstant.API_ACCESS_TOKEN,
        )}`,
      },
    });
    if (response.status >= 200 && response.status < 300) {
      return await response.data;
    } else {
      throw new Error(`${response.status}`);
    }
  } catch (error) {
    throw error;
  }
};

export const getInvoiceDetailsRequest = async (path, params) => {
  try {
    const response = await axios.get(path, {
      ...params,
      headers: {
        country: "USA",
        source: getDeviceName(),
        correlationId: generateUUID(),
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${storePersist.get(
          localConstant.API_ACCESS_TOKEN,
        )}`,
      },
    });
    if (response.status >= 200 && response.status < 300) {
      return await response.data;
    } else {
      throw new Error(`${response.status}`);
    }
  } catch (error) {
    throw error;
  }
};

export const getPaymentDetailsRequest = async (path, p) => {
  try {
    const response = await axios.post(
      path,
      {},
      {
        params: {
          ...p,
        },
        headers: {
          country: "USA",
          source: getDeviceName(),
          correlationId: generateUUID(),
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${storePersist.get(
            localConstant.API_ACCESS_TOKEN,
          )}`,
        },
      },
    );
    if (response.status >= 200 && response.status < 300) {
      return await response.data;
    } else {
      throw new Error(`${response.status}`);
    }
  } catch (error) {
    throw error;
  }
};

export const getPaymentDocumentRequest = async (path, p) => {
  try {
    const response = await axios.post(
      path,
      {},
      {
        params: {
          ...p,
        },
        headers: {
          country: "USA",
          source: getDeviceName(),
          correlationId: generateUUID(),
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${storePersist.get(
            localConstant.API_ACCESS_TOKEN,
          )}`,
          language: "en",
        },
      },
    );
    if (response.status >= 200 && response.status < 300) {
      return await response.data;
    } else {
      throw new Error(`${response.status}`);
    }
  } catch (error) {
    throw error;
  }
};

export const setHereAboutUsStatusRequest = async (path, body) => {
  try {
    const response = await axios.post(path, body, {
      headers: {
        country: "USA",
        source: getDeviceName(),
        correlationId: generateUUID(),
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${storePersist.get(
          localConstant.API_ACCESS_TOKEN,
        )}`,
        language: "en",
      },
    });
    if (response.status >= 200 && response.status < 300) {
      return await response.data;
    } else {
      throw new Error(`${response.status}`);
    }
  } catch (error) {
    throw error;
  }
};

export const getGatePinRequest = async (
  path,
  params,
  acceptHeader = "application/json",
  contentTypeHeader = "application/json",
) => {
  const headers = {
    "Content-Type": contentTypeHeader,
    country: "USA",
    source: getDeviceName(),
    correlationId: generateUUID(),
    Accept: acceptHeader,
    Authorization: `Bearer ${storePersist.get(localConstant.API_ACCESS_TOKEN)}`,
  };
  try {
    const response = await axios.get(path, {
      params,
      headers: headers,
    });
    if (response.status >= 200 && response.status < 300) {
      return await response.data;
    } else {
      throw new Error(`${response.errMsg}`);
    }
  } catch (error) {
    throw error;
  }
};

export const getGatePinLocationRequest = async (
  path,
  acceptHeader = "application/json",
  contentTypeHeader = "application/json",
) => {
  const headers = {
    "Content-Type": contentTypeHeader,
    country: "USA",
    source: getDeviceName(),
    correlationId: generateUUID(),
    Accept: acceptHeader,
    Authorization: `Bearer ${storePersist.get(localConstant.API_ACCESS_TOKEN)}`,
  };
  try {
    const response = await axios.get(path, {
      headers: headers,
    });
    if (response.status >= 200 && response.status < 300) {
      return await response.data;
    } else {
      throw new Error(`${response.errMsg}`);
    }
  } catch (error) {
    throw error;
  }
};

export const getApplicationSettings = async (path) => {
  try {
    const response = await axios.get(path, {});
    if (response.status >= 200 && response.status < 300) {
      return await response.data;
    } else {
      throw new Error(`${response.errMsg}`);
    }
  } catch (error) {
    throw error;
  }
};

export const fetchCustomerEsignStatus = async () => {
  try {
    const customerId = storePersist.get(localConstant.ENTITY_CUSTOMER_ID);
    const getEsignStatus = substituteUrlParams(apiConfig.getEsignStatus, {
      customerId,
    });
    const resp = await getRequestAuthenticationRequest(getEsignStatus);
    return resp;
  } catch (err) {
    enqueueSnackbar("Error Check Customer Stroge Agreement Status", {
      variant: "error",
      autoHideDuration: 3000,
      action: SnackbarConfig,
    });
  }
};
